import { Flex, Table } from 'components';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { IApprovalSchemeFormValues } from 'types/approver';
import { useTranslation } from 'react-i18next';
import { IExecutionProgressTracking } from 'types/executionProgressTrackings';
import { Button, Checkbox, FieldLabel, Select } from 'components/_form';
import { IOption } from 'components/_form/Select/Select';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faChevronUp,
  faPlusCircle,
  faTrash
} from '@fortawesome/free-solid-svg-icons';
import styled from 'styled-components';
import { useCallback, useMemo } from 'react';
import { handlePrevState } from 'utilities/handlePrevState';

const Buttons = styled(Flex)`
  width: fit-content;
  margin: 0 auto;
  gap: 8px;
  align-items: center;
`;

interface Props {
  executionProgressTrackings: IExecutionProgressTracking[];
  userFunctionsList: IOption[];
}

export const ApproversTable: React.FC<Props> = ({
  executionProgressTrackings,
  userFunctionsList
}) => {
  const {
    control,
    watch,
    formState: { errors }
  } = useFormContext<IApprovalSchemeFormValues>();

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'approvers_attributes'
  });

  const { t } = useTranslation();
  const approversAttributes = watch('approvers_attributes');

  const canAddApprover =
    userFunctionsList.length > 0 && fields.length < userFunctionsList.length;

  const handleAppendApprover = useCallback(() => {
    if (
      !userFunctionsList.length ||
      fields.length >= userFunctionsList.length
    ) {
      return;
    }

    append({
      execution_progress_tracking_approval_steps_attributes:
        executionProgressTrackings.map((executionProgressTracking) => ({
          execution_progress_tracking_id: executionProgressTracking.id,
          steps_to_approve: []
        }))
    });
  }, [
    append,
    executionProgressTrackings,
    fields.length,
    userFunctionsList.length
  ]);

  const header = [
    t('administrationTasksView.taskTypeApprovalStepsTableHeaders.approverRole'),
    t('administrationTasksView.taskTypeApprovalStepsTableHeaders.approveSteps'),
    ''
  ];

  const data = useMemo(
    () =>
      fields.map((field, index) => ({
        approverRole: (
          <Controller
            key={`approver-${field.id}`}
            control={control}
            name={`approvers_attributes.${index}.function_id`}
            render={({ field: { value, onChange } }) => {
              const availableOptions = userFunctionsList.filter(
                (userFunction) =>
                  !approversAttributes?.some(
                    (approver, approverIndex) =>
                      approverIndex !== index &&
                      approver.function_id?.toString() ===
                        userFunction.value?.toString()
                  )
              );
              const hasSelectableOptions = availableOptions.some(
                (option) => option.value?.toString() !== value?.toString()
              );

              return (
                <Select
                  options={availableOptions}
                  selectedOptions={userFunctionsList.find(
                    (userFunction) =>
                      userFunction.value?.toString() === value?.toString()
                  )}
                  onChange={(selectedOption) => {
                    if (!Array.isArray(selectedOption)) {
                      onChange(selectedOption.value);
                    }
                  }}
                  withoutMargin
                  openMenuOnClick={hasSelectableOptions}
                  openMenuOnFocus={false}
                  placeholder={t('buttons.choosePlaceholder') as string}
                  error={t(
                    errors.approvers_attributes?.[index]?.function_id
                      ?.message || ''
                  )}
                />
              );
            }}
          />
        ),
        approveSteps: (
          <Flex
            key={`steps-${field.id}`}
            gap="12px"
            flexWrap="wrap"
            justifyContent="center"
          >
            {executionProgressTrackings.map((executionProgressTracking) =>
              executionProgressTracking.kind.split('').map((letter) => (
                <Controller
                  key={`steps-${field.id}-${executionProgressTracking.id}-${letter}`}
                  control={control}
                  name={`approvers_attributes.${index}.execution_progress_tracking_approval_steps_attributes`}
                  render={({ field: { value: approvalSteps, onChange } }) => (
                    <Flex flexDirection="column" alignItems="center" gap="4px">
                      <FieldLabel centered width="fit-content">
                        {letter}
                      </FieldLabel>
                      <Checkbox
                        checked={
                          !!approvalSteps?.find(
                            (approvalStep) =>
                              approvalStep.execution_progress_tracking_id ===
                                executionProgressTracking.id &&
                              approvalStep.steps_to_approve.includes(letter)
                          )
                        }
                        onChange={() => {
                          onChange(
                            approvalSteps?.map((approvalStep) =>
                              approvalStep.execution_progress_tracking_id ===
                              executionProgressTracking.id
                                ? {
                                    ...approvalStep,
                                    steps_to_approve: handlePrevState(
                                      approvalStep.steps_to_approve,
                                      letter
                                    )
                                  }
                                : approvalStep
                            )
                          );
                        }}
                      />
                    </Flex>
                  )}
                />
              ))
            )}
          </Flex>
        ),
        actions: (
          <Buttons key={`actions-${field.id}`}>
            <Button
              variant="eucalyptus"
              icon={<FontAwesomeIcon icon={faTrash} />}
              onClick={() => {
                remove(index);
              }}
            />
            <Flex flexDirection="column" gap="2px">
              <Button
                bordered
                transparent
                icon={<FontAwesomeIcon icon={faChevronUp} />}
                minHeight="17px"
                disabled={index === 0}
                onClick={() => {
                  move(index, index - 1);
                }}
              />
              <Button
                bordered
                transparent
                icon={<FontAwesomeIcon icon={faChevronDown} />}
                minHeight="17px"
                disabled={index === fields.length - 1}
                onClick={() => {
                  move(index, index + 1);
                }}
              />
            </Flex>
          </Buttons>
        )
      })),
    [
      approversAttributes,
      control,
      errors.approvers_attributes,
      executionProgressTrackings,
      fields,
      move,
      remove,
      t,
      userFunctionsList
    ]
  );

  return (
    <Flex flex={1} minWidth={0} flexDirection="column" gap="8px">
      <Flex justifyContent="flex-end">
        <Button
          variant="green"
          disabled={!canAddApprover}
          onClick={handleAppendApprover}
        >
          <Flex gap="4px" alignItems="center">
            <FontAwesomeIcon icon={faPlusCircle} />
            {t('common.add')}
          </Flex>
        </Button>
      </Flex>
      <Table
        header={header}
        data={data}
        sortTable={false}
        rightFixedKeys={[header.length - 1]}
      />
    </Flex>
  );
};
